import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import { ResourceLoading } from "../../utils/ResourceLoading";
import {
  TruckIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  MapPinIcon,
  PencilIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { dateToString, ItemStatus, axiosWithToast } from "../../Utils";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { driverVehicleAction } from "../../../pages/dashboard/driving/drivers/utils";
import { ConfirmDialog } from "../../utils/Prompt";
import { fetchDrivingDrivers } from "../../../api/driving/driver";

export const VehiclesTable = (props) => {
  const {
    itemState,
    onPageChange,
    itemsCount,
    currentCount,
    page,
    perPage,
    onItemsSelect,
    onEditVehicle,
    fleetAutomileActions = false,
    onRefreshVehicles,
  } = props;
  const { data, isLoading, errors } = itemState;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [checkoutVehicle, setCheckoutVehicle] = useState(null);
  const [driverPickVehicle, setDriverPickVehicle] = useState(null);
  const [driverCandidates, setDriverCandidates] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);

  useEffect(() => {
    if (!driverPickVehicle) return;
    let cancelled = false;
    setDriversLoading(true);
    fetchDrivingDrivers({
      page: 1,
      per_page: 250,
      sortBy: "created_at",
      sort: "desc",
    })
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data || [];
        const eligible = fleetAutomileActions
          ? rows.filter((d) => d.user?.id)
          : rows.filter(
              (d) => d.user?.id && !(d.checked_into_vehicle && d.checked_into_vehicle.id)
            );
        setDriverCandidates(eligible);
      })
      .catch(() => {
        if (!cancelled) setDriverCandidates([]);
      })
      .finally(() => {
        if (!cancelled) setDriversLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [driverPickVehicle, fleetAutomileActions]);

  const refresh = () => {
    if (onRefreshVehicles) onRefreshVehicles();
  };

  const runFleetCheckout = async () => {
    const v = checkoutVehicle;
    const driver = v?.checked_in_driver;
    const userId = driver?.user?.id;
    const driverId = driver?.id ?? v?.checked_in_driver_id;
    if (!userId && !driverId) {
      toast.error(t("automile.linkUser"));
      setCheckoutVehicle(null);
      return;
    }
    try {
      await axiosWithToast(
        driverVehicleAction({
          userId: userId || undefined,
          driverId: userId ? undefined : driverId,
          vehicleId: v.id,
          action: "checkout",
        }),
        null,
        null,
        () => refresh()
      );
    } finally {
      setCheckoutVehicle(null);
    }
  };

  const runFleetCheckin = async (driverRow) => {
    if (!driverPickVehicle || !driverRow?.user?.id) return;
    try {
      await axiosWithToast(
        driverVehicleAction({
          userId: driverRow.user.id,
          vehicleId: driverPickVehicle.id,
          action: "checkin",
        }),
        null,
        null,
        () => refresh()
      );
    } finally {
      setDriverPickVehicle(null);
    }
  };

  const VehicleCard = ({ item }) => {
    const checkedInDriverId = item?.checked_in_driver?.id ?? item?.checked_in_driver_id;
    const canCheckOut = Boolean(item?.checked_in_driver?.user?.id ?? checkedInDriverId);
    const hasCheckedInDriver = Boolean(checkedInDriverId);
    const canCheckIn =
      item?.active !== false && (!hasCheckedInDriver || fleetAutomileActions);

    return (
      <div className="shadow sm:overflow-hidden sm:rounded-md hover:shadow-lg transition-shadow duration-200">
        <div
          className="cursor-pointer"
          onClick={() => navigate("/dashboard/driving/vehicles/" + item?.id)}
        >
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TruckIcon className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {item?.number_plate || "Missing Plate"}
                    </h3>
                    <ItemStatus item={item} />
                  </div>
                  <p className="text-sm text-gray-600">{item?.nickname || item?.displayTitle}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {item?.make && item?.model && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <TruckIcon className="w-3 h-3 mr-1" />
                    {item.make} {item.model}
                  </span>
                )}
                {onEditVehicle && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditVehicle(item);
                    }}
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  >
                    <PencilIcon className="w-3 h-3 mr-1" />
                    Edit
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {item?.make && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <TruckIcon className="w-4 h-4 mr-1" />
                    {t("Make")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{item.make}</dd>
                </div>
              )}

              {item?.model && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <DocumentTextIcon className="w-4 h-4 mr-1" />
                    {t("Model")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{item.model}</dd>
                </div>
              )}

              {item?.model_year && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    {t("Model Year")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{item.model_year}</dd>
                </div>
              )}

              {item?.current_odometer_in_kilometers && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    {t("Current Odometer")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {item.current_odometer_in_kilometers.toLocaleString()} km
                  </dd>
                </div>
              )}

              {item?.distance_driven ? (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <MapPinIcon className="w-4 h-4 mr-1" />
                    {t("Distance Driven")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {item.distance_driven.toLocaleString()} km
                  </dd>
                </div>
              ) : null}

              {item?.owner_driver && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <UserIcon className="w-4 h-4 mr-1" />
                    {t("Owner Driver")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {item.owner_driver.displayTitle || item.owner_driver.name || "-"}
                  </dd>
                </div>
              )}

              {item?.checked_in_driver && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                    {t("Checked In Driver")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {item.checked_in_driver.displayTitle || item.checked_in_driver.name || "-"}
                  </dd>
                </div>
              )}

              {item?.checked_in_date_time && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    {t("Checked In")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{dateToString(item.checked_in_date_time)}</dd>
                </div>
              )}

              {item?.next_inspection_date && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    {t("Next Inspection Date")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {dateToString(item.next_inspection_date) || item.next_inspection_date}
                  </dd>
                </div>
              )}

              {item?.created_at && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    {t("Created")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{dateToString(item.created_at)}</dd>
                </div>
              )}
            </div>

            {item?.description && (
              <div className="mt-6">
                <dt className="text-sm font-medium text-gray-500">{t("Description")}</dt>
                <dd className="mt-1 text-sm text-gray-900 line-clamp-3">{item.description}</dd>
              </div>
            )}
          </div>
        </div>

        {fleetAutomileActions && (
          <div
            className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex flex-wrap gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {canCheckOut && (
              <button
                type="button"
                onClick={() => setCheckoutVehicle(item)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 text-red-600" />
                {t("automile.checkOutVehicle")}
              </button>
            )}
            {canCheckIn && (
              <button
                type="button"
                onClick={() => setDriverPickVehicle(item)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100"
              >
                <ArrowLeftOnRectangleIcon className="h-4 w-4 text-green-600" />
                {t("automile.checkInDriver")}
              </button>
            )}
            {item?.checked_in_driver && !checkedInDriverId && (
              <span className="text-xs text-amber-700 self-center">{t("automile.linkUser")}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const Stats = () => (
    <>
      {data && data.length > 0 && (
        <div className="space-y-4 mt-5">
          {data?.map((item) => (
            <VehicleCard key={item?.id} item={item} />
          ))}
        </div>
      )}
    </>
  );

  if (isLoading) return <ResourceLoading />;

  return (
    <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
      <div className="shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          {isLoading ? (
            <ResourceLoading size="small" />
          ) : errors ? (
            <div className="text-center py-8 text-red-500">
              <div className="text-lg">Could not load vehicles</div>
            </div>
          ) : data && data.length > 0 ? (
            <Stats />
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="text-lg">No vehicles found</div>
              <div className="text-sm">Create a new vehicle</div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(checkoutVehicle)}
        onClose={() => setCheckoutVehicle(null)}
        onConfirm={runFleetCheckout}
        title={t("automile.checkoutConfirmTitle")}
        message={t("automile.checkoutConfirmMessage")}
        confirmText={t("automile.checkoutConfirm")}
        cancelText={t("automile.checkoutCancel")}
      />

      <Transition.Root show={Boolean(driverPickVehicle)} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[3400]"
          onClose={() => setDriverPickVehicle(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40" />
          </Transition.Child>

          <div className="fixed inset-0 z-[3400] flex items-end justify-center overflow-y-auto p-0 sm:items-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl max-h-[min(90vh,100%)] overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  {t("automile.checkInDriver")}
                </Dialog.Title>
                <p className="mt-1 text-sm text-gray-500">
                  {driverPickVehicle?.displayTitle || driverPickVehicle?.number_plate}
                </p>
                <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {driversLoading ? (
                    <ResourceLoading size="small" />
                  ) : driverCandidates.length === 0 ? (
                    <p className="text-sm text-gray-500">{t("automile.noDriversFreeForCheckin")}</p>
                  ) : (
                    driverCandidates.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => runFleetCheckin(d)}
                        className="w-full text-left rounded-lg border border-gray-200 px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        <div className="font-medium text-gray-900">
                          {d.user?.displayTitle || d.name}
                        </div>
                        {d.user?.email && (
                          <div className="text-xs text-gray-500">{d.user.email}</div>
                        )}
                      </button>
                    ))
                  )}
                </div>
                <button
                  type="button"
                  className="mt-4 w-full rounded-lg border border-gray-300 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setDriverPickVehicle(null)}
                >
                  {t("Close")}
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

VehiclesTable.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "createdAt",
  onEditVehicle: null,
  fleetAutomileActions: false,
  onRefreshVehicles: null,
};
